import {
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalTokenInput,
    useApprovalTokenCreateMutation,
} from "graphql-sdk";
import { defaultApprovalToken, schemaApprovalToken } from "lib";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { Form } from "../form";

type ScreenProps = RootStackScreenProps<"APPROVAL_TOKEN_CREATE">;

export function ApprovalTokenCreateScreen({
    navigation: { navigate },
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.approval");

    const [t] = useTranslation();
    const [approvalTokenCreate] = useApprovalTokenCreateMutation();
    const form = useForm<GQApprovalTokenInput>(
        schemaApprovalToken,
        defaultApprovalToken
    );

    const [{ values }, { setDefaultValues, reset }] = form;
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();

    const handleCreate = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(
            async () =>
                await approvalTokenCreate({
                    variables: {
                        approvalToken: {
                            name: values.name,
                            approvalTokenGroupId: values.approvalTokenGroupId,
                            value: values.value,
                            type: values.type,
                            active: values.active,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                reset();
                navigate("APPROVALS", { refetch: true, tabIndex: 1 });
            }
        );
    }, [values, handleMutationError, t, approvalTokenCreate, reset, navigate]);

    useEffect(() => {
        if (values) {
            return;
        }
        setDefaultValues({
            active: true,
            name: "",
            value: "",
            type: "",
        });
    }, [setDefaultValues, values]);

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleCreate}
                        submitButton={["common.save", "Save"]}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
